import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    RefreshCw, Users, Home, Wallet, TrendingUp, Search, AlertTriangle,
    LayoutDashboard, Table2, X, Filter, Flame,
} from 'lucide-react';
import toast from 'react-hot-toast';
import PortfolioAnalyticsPanel from './PortfolioAnalyticsPanel';
import InvestorProfilePanel from './InvestorProfilePanel';
import { fetchInvestors } from '../../investors/api/investorsApi';

// ── Formatters ──────────────────────────────────────────────────────────────
const fmtCompact = (n) => {
    if (n === null || n === undefined || isNaN(Number(n))) return '—';
    const v = Number(n);
    const sign = v < 0 ? '-' : '';
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
    return `${sign}$${Math.round(abs).toLocaleString()}`;
};
const fmtMoney = (n, signed = false) => {
    if (n === null || n === undefined || isNaN(Number(n))) return '—';
    const v = Math.round(Number(n));
    const sign = signed && v > 0 ? '+' : v < 0 ? '-' : '';
    return `${sign}$${Math.abs(v).toLocaleString()}`;
};
const fmtPct = (n) => (n === null || n === undefined || isNaN(Number(n)) ? 'N/A' : `${Number(n).toFixed(1)}%`);
const fmtNum = (n) => (Number(n) || 0).toLocaleString();
const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Kpi = ({ label, value, sub, tone, Icon, accent = '#2563eb' }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: `${accent}1a`, color: accent }}><Icon className="w-4 h-4" /></span>
            <span className="text-xs font-medium text-gray-500">{label}</span>
        </div>
        <div className={`text-2xl font-bold ${tone === 'g' ? 'text-emerald-600' : tone === 'r' ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
);

const SORT_OPTIONS = [
    { key: 'portfolio_value', label: 'Portfolio value' },
    { key: 'total_equity', label: 'Total equity' },
    { key: 'accessible_equity', label: 'Accessible equity' },
    { key: 'total_debt', label: 'Total debt' },
    { key: 'monthly_cashflow', label: 'Cashflow' },
    { key: 'annual_rent', label: 'Gross rent' },
    { key: 'avg_net_yield', label: 'Net yield' },
    { key: 'buying_score', label: 'Buying score' },
    { key: 'property_count', label: 'Property count' },
];

// Quick-toggle filter chips (Level-2). Each toggles a key in the filters object.
const QUICK_FILTERS = [
    { key: 'fixed_expiring', val: true, label: 'Fixed expiring' },
    { key: 'cashflow', val: 'negative', label: 'Cashflow negative' },
    { key: 'cashflow', val: 'positive', label: 'Positive cashflow' },
    { key: 'high_lvr', val: true, label: 'High LVR' },
    { key: 'likely', val: true, label: 'Likely buyer' },
    { key: 'used_calculator', val: true, label: 'Used calculator' },
    { key: 'asked_geevee', val: true, label: 'Asked GeeVee' },
    { key: 'enquired', val: true, label: 'Enquired' },
    { key: 'entity', val: 'smsf', label: 'SMSF' },
    { key: 'entity', val: 'trust', label: 'Trust' },
    { key: 'managed', val: 'managed', label: 'Prof. managed' },
    { key: 'managed', val: 'self', label: 'Self-managed' },
];

const InvestorAnalyticsPanel = ({ startDate, endDate }) => {
    const [selectedId, setSelectedId] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('portfolio_value');
    const [filters, setFilters] = useState({});
    const [drillLabel, setDrillLabel] = useState(null);
    const tokenRef = useRef(0);
    const tableRef = useRef(null);

    useEffect(() => {
        const tm = setTimeout(() => setSearch(searchInput), 350);
        return () => clearTimeout(tm);
    }, [searchInput]);

    const load = useCallback(() => {
        const myToken = ++tokenRef.current;
        setLoading(true);
        setError(false);
        fetchInvestors({ search, sort, filters })
            .then((d) => { if (myToken === tokenRef.current) setData(d); })
            .catch((err) => { if (myToken === tokenRef.current) { console.error(err); setError(true); } })
            .finally(() => { if (myToken === tokenRef.current) setLoading(false); });
    }, [search, sort, filters]);

    useEffect(() => { load(); }, [load]);

    // Drill-down from a Level-1 card/breakdown row.
    const handleDrill = useCallback((patch, label) => {
        const { sort: sortPatch, ...rest } = patch || {};
        if (sortPatch) setSort(sortPatch);
        setFilters((f) => ({ ...f, ...rest }));
        setDrillLabel(label || null);
        toast.success(label ? `Filtered: ${label}` : 'Filtered investors');
        // Bring the table into view.
        setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    }, []);

    const toggleQuick = (key, val) => {
        setDrillLabel(null);
        setFilters((f) => {
            const next = { ...f };
            if (next[key] === val) delete next[key];
            else next[key] = val;
            return next;
        });
    };

    const clearFilters = () => { setFilters({}); setDrillLabel(null); };
    const activeCount = Object.keys(filters).length;

    // ── Level 3 ──
    if (selectedId) {
        return <InvestorProfilePanel investorId={selectedId} onBack={() => setSelectedId(null)} />;
    }

    const t = data?.totals || {};
    const investors = data?.investors || [];

    return (
        <div className="space-y-8">
            {/* LEVEL 1 — Platform overview (clickable) */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <LayoutDashboard className="w-4 h-4 text-[#2563eb]" />
                    <h3 className="text-sm font-bold text-gray-900">Platform investor overview</h3>
                    <span className="text-xs text-gray-400">— click any card or bar to drill into the matching investors</span>
                </div>
                <PortfolioAnalyticsPanel startDate={startDate} endDate={endDate} onDrill={handleDrill} />
            </div>

            {/* LEVEL 2 — Investor table */}
            <div ref={tableRef} className="scroll-mt-6">
                <div className="flex items-center gap-2 mb-3">
                    <Table2 className="w-4 h-4 text-[#0e9f6e]" />
                    <h3 className="text-sm font-bold text-gray-900">Investors</h3>
                    <span className="text-xs text-gray-400">— {fmtNum(t.investors)} matching · click a row for the full profile</span>
                </div>

                {/* Filtered totals strip */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <Kpi label="Investors" value={fmtNum(t.investors)} sub={`${fmtNum(t.property_count)} properties`} Icon={Users} accent="#2563eb" />
                    <Kpi label="Portfolio value" value={fmtCompact(t.portfolio_value)} sub={t.avg_lvr != null ? `Avg LVR ${fmtPct(t.avg_lvr)}` : null} Icon={Home} accent="#0e9f6e" />
                    <Kpi label="Total equity" value={fmtCompact(t.total_equity)} sub={`Debt ${fmtCompact(t.total_debt)}`} Icon={Wallet} accent="#7c3aed" />
                    <Kpi label="Accessible equity" tone="g" value={fmtCompact(t.accessible_equity)} sub="at 80% LVR" Icon={TrendingUp} accent="#0e9f6e" />
                </div>

                {/* Controls */}
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-3">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search investors by name, email or phone…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent" />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-500 whitespace-nowrap">Sort by</label>
                        <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm border border-gray-200 rounded-lg py-2 pl-3 pr-8 focus:outline-none focus:ring-2 focus:ring-[#55C5D0] bg-white">
                            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                    </div>
                </div>

                {/* Quick filters */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Filter className="w-3.5 h-3.5 text-gray-400" />
                    {QUICK_FILTERS.map((qf) => {
                        const active = filters[qf.key] === qf.val;
                        return (
                            <button key={`${qf.key}:${qf.val}`} onClick={() => toggleQuick(qf.key, qf.val)}
                                className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                                {qf.label}
                            </button>
                        );
                    })}
                    {(activeCount > 0 || drillLabel) && (
                        <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600 ml-1">
                            <X className="w-3.5 h-3.5" /> Clear{drillLabel ? ` · ${drillLabel}` : ` (${activeCount})`}
                        </button>
                    )}
                </div>

                {/* States */}
                {loading && !data ? (
                    <div className="flex items-center justify-center py-16 text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading investors…</div>
                ) : error ? (
                    <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-700">Couldn't load investors</p>
                        <button onClick={load} className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-[#2563eb] hover:bg-blue-700"><RefreshCw className="w-4 h-4" /> Retry</button>
                    </div>
                ) : investors.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                        <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-700">No investors match</p>
                        <p className="text-xs text-gray-400 mt-1">{activeCount || search ? 'Try clearing filters or the search term.' : 'Investors appear here once buyers start tracking portfolios.'}</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50">
                                            <th className="px-4 py-3">Investor</th>
                                            <th className="px-4 py-3 text-right">Props</th>
                                            <th className="px-4 py-3 text-right">Value</th>
                                            <th className="px-4 py-3 text-right">Equity</th>
                                            <th className="px-4 py-3 text-right">Accessible</th>
                                            <th className="px-4 py-3 text-right">Cashflow / mo</th>
                                            <th className="px-4 py-3 text-right">Net yield</th>
                                            <th className="px-4 py-3 text-right">Score</th>
                                            <th className="px-4 py-3">Finance</th>
                                            <th className="px-4 py-3 text-right">Last activity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {investors.map((inv) => {
                                            const cf = Number(inv.monthly_cashflow);
                                            const cfTone = isNaN(cf) ? '' : cf >= 0 ? 'text-emerald-600' : 'text-red-600';
                                            return (
                                                <tr key={inv.id} onClick={() => setSelectedId(inv.id)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors">
                                                    <td className="px-4 py-3">
                                                        <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                                                            {inv.likely_transaction && <Flame className="w-3.5 h-3.5 text-red-500 shrink-0" title="Likely 90-day buyer" />}
                                                            {inv.name || 'Unnamed investor'}
                                                        </div>
                                                        <div className="text-xs text-gray-400">{inv.email || inv.phone || '—'}</div>
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtNum(inv.property_count)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtCompact(inv.portfolio_value)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtCompact(inv.total_equity)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtCompact(inv.accessible_equity)}</td>
                                                    <td className={`px-4 py-3 text-right tabular-nums font-medium ${cfTone}`}>{fmtMoney(inv.monthly_cashflow, true)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtPct(inv.avg_net_yield)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{inv.buying_score != null ? inv.buying_score : '—'}</td>
                                                    <td className="px-4 py-3 text-xs text-gray-500">{inv.finance_label || '—'}</td>
                                                    <td className="px-4 py-3 text-right text-xs text-gray-400">{fmtDate(inv.last_activity_at)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile cards */}
                        <div className="md:hidden space-y-3">
                            {investors.map((inv) => {
                                const cf = Number(inv.monthly_cashflow);
                                const cfTone = isNaN(cf) ? 'text-gray-700' : cf >= 0 ? 'text-emerald-600' : 'text-red-600';
                                return (
                                    <button key={inv.id} onClick={() => setSelectedId(inv.id)} className="w-full text-left bg-white rounded-xl border border-gray-200 p-4 hover:bg-gray-50">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="font-semibold text-gray-900 truncate flex items-center gap-1.5">
                                                    {inv.likely_transaction && <Flame className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                                                    {inv.name || 'Unnamed investor'}
                                                </div>
                                                <div className="text-xs text-gray-400 truncate">{inv.email || inv.phone || '—'}</div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="font-bold text-gray-900">{fmtCompact(inv.portfolio_value)}</div>
                                                <div className="text-[11px] text-gray-400">{inv.buying_score != null ? `score ${inv.buying_score}` : 'value'}</div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                                            <div><div className="text-gray-400">Equity</div><div className="font-medium text-gray-800">{fmtCompact(inv.total_equity)}</div></div>
                                            <div><div className="text-gray-400">Accessible</div><div className="font-medium text-gray-800">{fmtCompact(inv.accessible_equity)}</div></div>
                                            <div><div className="text-gray-400">Cashflow</div><div className={`font-medium ${cfTone}`}>{fmtMoney(inv.monthly_cashflow, true)}</div></div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default InvestorAnalyticsPanel;
